import { useCallback, useRef } from "react";

/**
 * 質問入力欄に渡す引数
 * @property draft 入力中のテキスト
 * @property questionList 確定済みの質問一覧
 * @property setDraft 入力中テキストの更新関数
 * @property setQuestions 質問一覧の更新関数
 */
type UseQuestionChipInputParams = {
  draft: string;
  questionList: string[];
  setDraft: (value: string) => void;
  setQuestions: (value: string[]) => void;
};

/**
 * 質問入力欄の状態とイベントを管理するhook
 * @param draft 入力中のテキスト
 * @param questionList 確定済みの質問一覧
 * @param setDraft 入力中テキストの更新関数
 * @param setQuestions 質問一覧の更新関数
 * @return 入力参照と操作関数
 */
export const useQuestionChipInput = ({
  draft,
  questionList,
  setDraft,
  setQuestions,
}: UseQuestionChipInputParams) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  /**
   * 入力欄へフォーカスを移動する
   * @return フォーカス処理の完了
   */
  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  /**
   * クリップボードからテキストを抽出する
   * HTMLの表が含まれる場合は行列を維持してプレーンテキスト化する
   * @param event 貼り付けイベント
   * @return 整形後の貼り付け文字列
   */
  const extractPastedText = useCallback((event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const html = event.clipboardData.getData("text/html");

    if (html) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const rows = Array.from(doc.querySelectorAll("tr"));

      if (rows.length > 0) {
        return rows
          .map((row) => {
            const cells = Array.from(row.querySelectorAll("th, td"));

            return cells
              .map((cell) => (cell.textContent ?? "").replace(/\r?\n/g, " ").trim())
              .join("\t");
          })
          .join("\n");
      }
    }

    return event.clipboardData.getData("text/plain").replace(/\r\n/g, "\n");
  }, []);

  /**
   * テキストを改行ごとに分割し、空白と空行を除去する
   * @param text 入力テキスト
   * @return 整形済みの行一覧
   */
  const lineBreakProcessing = useCallback((text: string) => {
    return text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }, []);

  /**
   * 質問一覧と下書きを統合して送信用の質問リストを生成する
   * @return 送信用の質問リスト
   */
  const getQuestionsForSubmit = useCallback(() => {
    const processedLines = lineBreakProcessing(draft);

    if (processedLines.length === 0) {
      return questionList;
    }

    return [...questionList, ...processedLines];
  }, [draft, lineBreakProcessing, questionList]);

  /**
   * 貼り付け文字列を整形して下書きに反映する
   * @param event 貼り付けイベント
   * @return 下書き反映の完了
   */
  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      event.preventDefault();
      const pastedText = extractPastedText(event);
      setDraft(pastedText);
    },
    [extractPastedText, setDraft]
  );

  /**
   * 下書きを質問一覧へ追加し、入力欄をクリアする
   * @return 質問追加処理の完了
   */
  const commitDraftToQuestionList = useCallback(() => {
    const nextQuestions = getQuestionsForSubmit();

    if (nextQuestions.length === questionList.length) {
      focusInput();
      return;
    }

    setQuestions(nextQuestions);
    setDraft("");
    focusInput();
  }, [focusInput, getQuestionsForSubmit, questionList.length, setDraft, setQuestions]);

  /**
   * Enterで質問追加、Shift + Enterで改行する
   * @param event キー入力イベント
   * @return キー入力処理の完了
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && event.shiftKey) {
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        commitDraftToQuestionList();
      }
    },
    [commitDraftToQuestionList]
  );

  return {
    inputRef,
    focusInput,
    handlePaste,
    handleKeyDown,
    getQuestionsForSubmit,
  };
};
